/**
 * Browser runtime entry point
 *
 * Creates and exports the runtime API for browser use.
 * Internal modules (webRuntime, runtimeStore) are not exported directly.
 */

import { WebRuntime } from './WebRuntime';
import { ServerSync } from '../../serverClient/ServerSync';
import { RuntimeStore, type DefaultView } from '../RuntimeStore';
import { HistoryManager } from '../HistoryManager';
import { runtimeApi } from '../runtimeApi';
import { config } from '@/config';
import { logger } from '@/logging';
import { TransportService } from '@/ui/services/TransportService';
import { getWebTransport } from '@/env/web/AudioContext';
import { getThree } from '@/plugins/threejs/threejsPlugin';
import { PerformanceRenderer } from '@/ui/components/renderers/PerformanceRenderer';
import { Polyline2DRenderer } from '@/ui/components/renderers/Polyline2DRenderer';
import { SpatialStore } from '@/runtime/SpatialStore';
import { registerVideoRenderer, unregisterVideoEntry, getVideoRenderer, getAllVideoEntries } from '@/plugins/video/videoRegistry';
import { JukeboxController } from '@/runtime/JukeboxController';
import { captureProjectSnapshot, type TweaksFile } from '@/nodes/PresetManager';

/**
 * Get session ID from URL path
 * e.g., http://localhost:5173/monk -> 'monk'
 * Falls back to 'default' on the root path to mirror server-side default.
 */
function getSessionFromUrl(): string {
  const path = window.location.pathname;
  return path.split('/')[1] || 'default';
}

const sessionId = getSessionFromUrl()

const runtimeStore = new RuntimeStore();
const spatialStore = new SpatialStore();
const webRuntime = new WebRuntime(runtimeStore);

logger.setLogLevel(config.logLevel);

export const historyManager = new HistoryManager(webRuntime);


// Persist sync setting in localStorage
const SYNC_STORAGE_KEY = 'tweakable-sync-enabled';
const savedSyncEnabled = localStorage.getItem(SYNC_STORAGE_KEY) === 'true';

export const serverSync = new ServerSync({
  sessionId,
  store: runtimeStore,
  onWebSocketUpdate: (session) => {
    // Apply server state to runtime (e.g., after project load)

    // Apply viewport BEFORE runtime update so GraphCanvas sees it on mount
    if (session.viewport) {
      for (const [instanceId, vp] of Object.entries(session.viewport)) {
        runtimeStore.setViewport(instanceId, vp as { x: number; y: number; zoom: number });
      }
    }

    // Apply positions
    if (session.positions) {
      const firstValue = Object.values(session.positions)[0] as Record<string, unknown> | undefined;
      const isLegacyFormat = firstValue && 'x' in firstValue && 'y' in firstValue;

      if (isLegacyFormat) {
        runtimeStore.setPositions('root', session.positions as Record<string, { x: number; y: number }>);
      } else {
        for (const [defId, defPositions] of Object.entries(session.positions)) {
          runtimeStore.setPositions(defId, defPositions as Record<string, { x: number; y: number }>);
        }
      }
    }

    // Apply definitions
    webRuntime.setState({ definitions: session.definitions });
  }
});

// Restore sync setting from localStorage
serverSync.setSyncEnabled(savedSyncEnabled);

// Initialize runtime with empty root immediately (so it's never undefined)
webRuntime.setState({ definitions: { root: { id: 'root', nodes: {}, connections: [], api: {}, config: {} } } });

// Fetch library definitions (always needed) and session (only if sync enabled)
serverSync.init().then(({ session }) => {
  // Only load server session if sync is enabled
  if (serverSync.syncEnabled) {
    webRuntime.setState({ definitions: session.definitions });
  }
});

const baseApi = runtimeApi(webRuntime, runtimeStore);

// Create transport service with transport from environment
export const transportService = new TransportService(getWebTransport());

// Jukebox controller for snapshot cycling
export const jukeboxController = new JukeboxController(
  getWebTransport(),
  (snapshot) => webRuntime.recallSnapshot(snapshot)
);

export const webRuntimeApi = {
  ...baseApi,

  // Clear with server sync
  clear(): void {
    baseApi.clear();
    serverSync.serverApi.clear().catch(err => {
      logger.error('webRuntimeApi', 'Failed to sync clear to server', err);
    });
  },

  // Runtime methods
  getPluginRegistry: () => webRuntime.getPluginRegistry(),
  getGraphDefinition: (definitionId: string) => webRuntime.getGraphDefinition(definitionId),
  setNodeLogLevel: (instanceId: string, nodeId: string, level: string) =>
    webRuntime.setNodeLogLevel(instanceId, nodeId, level),
  getUnexposeAffectedItems: (definitionId: string, exposedAs: string) =>
    webRuntime.getUnexposeAffectedItems(definitionId, exposedAs),

  // Store subscriptions
  subscribe: {
    ready: (callback: Parameters<typeof runtimeStore.subscribeReady>[0]) =>
      runtimeStore.subscribeReady(callback),
    values: (callback: Parameters<typeof runtimeStore.subscribeValues>[0]) =>
      runtimeStore.subscribeValues(callback),
    definition: (callback: Parameters<typeof runtimeStore.subscribeDefinition>[0]) =>
      runtimeStore.subscribeDefinition(callback),
    beforeDispose: (callback: Parameters<typeof runtimeStore.subscribeBeforeDispose>[0]) =>
      runtimeStore.subscribeBeforeDispose(callback),
  },

  // Store state
  getInstanceState: (instanceId: string) => runtimeStore.getInstanceState(instanceId),
  getDefinitionState: (definitionId: string) => runtimeStore.getDefinitionState(definitionId),
  getDefinition: (definitionId: string) => runtimeStore.getDefinition(definitionId),
  getDefinitionIds: () => runtimeStore.getDefinitionIds(),

  // Store position/viewport
  savePosition: (definitionId: string, nodeId: string, x: number, y: number) =>
    runtimeStore.savePosition(definitionId, nodeId, x, y),
  setPositions: (definitionId: string, positions: Record<string, { x: number; y: number }>) =>
    runtimeStore.setPositions(definitionId, positions),
  getViewport: (instanceId: string) => runtimeStore.getViewport(instanceId),
  setViewport: (instanceId: string, viewport: { x: number; y: number; zoom: number }) =>
    runtimeStore.setViewport(instanceId, viewport),

  // Server sync toggle (persisted in localStorage)
  get syncEnabled() { return serverSync.syncEnabled; },
  setSyncEnabled: (enabled: boolean) => {
    serverSync.setSyncEnabled(enabled);
    localStorage.setItem(SYNC_STORAGE_KEY, String(enabled));
  },

  // Default view mode (project-level)
  getDefaultView: (): DefaultView => runtimeStore.getDefaultView(),
  setDefaultView: (mode: DefaultView) => runtimeStore.setDefaultView(mode),

  // Get local session state for saving
  getLocalSession: () => {
    // Flush active 3D renderer orbits to store before saving
    const suffix = '.' + webRuntimeApi.__PERF3D_KEY;
    for (const [key, entry] of getAllVideoEntries()) {
      if (!key.endsWith(suffix)) continue;
      const instanceId = key.slice(0, -suffix.length);
      const renderer = entry.getRenderer() as PerformanceRenderer | undefined;
      if (!renderer) continue;
      const defId = runtimeStore.getInstanceDefinitionId(instanceId) ?? instanceId;
      const orbit = renderer.getOrbit();
      const existing = runtimeStore.getPerformanceView(defId);
      const activeCamera = renderer.getActiveCamera();
      runtimeStore.setPerformanceView(defId, {
        cameras: { ...existing?.cameras, [activeCamera]: orbit },
        activeCamera,
      });
    }
    const session = runtimeStore.getLocalSession();
    return { ...session, spatial: spatialStore.toJSON() };
  },

  // Load a project (from HTTP response) - fresh instantiation
  loadProject: (project: {
    definitions: Record<string, any>;
    positions?: Record<string, { x: number; y: number }> | Record<string, Record<string, { x: number; y: number }>>;
    viewport?: Record<string, { x: number; y: number; zoom: number }>;
    performance?: Record<string, any>;
    spatial?: Record<string, { x: number; y: number; z: number }>;
    defaultView?: DefaultView;
  }) => {
    // Clear store state before loading new project
    // This ensures old positions/viewports don't persist
    runtimeStore.clearForNewProject();
    spatialStore.clear();

    // Restore spatial positions before runtime load
    if (project.spatial) {
      spatialStore.fromJSON(project.spatial);
    }

    // Restore performance view state and default view before runtime load
    if (project.performance) {
      for (const [defId, state] of Object.entries(project.performance)) {
        runtimeStore.setPerformanceView(defId, state);
      }
    }
    if (project.defaultView) {
      runtimeStore.setDefaultView(project.defaultView);
    }
    // Apply viewport BEFORE runtime load so GraphCanvas sees it on mount
    if (project.viewport) {
      for (const [instanceId, vp] of Object.entries(project.viewport)) {
        runtimeStore.setViewport(instanceId, vp);
      }
    }

    // Apply positions BEFORE runtime load so they're available during instantiation
    if (project.positions) {
      const firstValue = Object.values(project.positions)[0];
      const isLegacyFormat = firstValue && 'x' in firstValue && 'y' in firstValue;

      if (isLegacyFormat) {
        // Legacy: flat positions { nodeId: {x, y} } - apply to root only
        runtimeStore.setPositions('root', project.positions as Record<string, { x: number; y: number }>);
      } else {
        // New format: { definitionId: { nodeId: {x, y} } }
        for (const [defId, defPositions] of Object.entries(project.positions)) {
          runtimeStore.setPositions(defId, defPositions as Record<string, { x: number; y: number }>);
        }
      }
    }

    // Dispose and re-instantiate fresh
    webRuntime.loadProject(project.definitions);

    // Clear undo history since we loaded a new project
    historyManager.clear();
  },

  // Reset to empty project
  newProject: () => {
    runtimeStore.clearForNewProject();
    spatialStore.clear();
    jukeboxController.clear();
    webRuntime.loadProject({
      root: { id: 'root', nodes: {}, connections: [], api: {}, config: {} }
    });
    historyManager.clear();
  },

  // ─── Performance 3D view ───

  /** Performance view registry key suffix */
  __PERF3D_KEY: '__perf3d',

  activatePerformanceView(instanceId: string, canvas: HTMLCanvasElement): void {
    const three = getThree();
    if (!three) {
      logger.error('webRuntimeApi', 'Cannot activate performance view — Three.js not loaded');
      return;
    }
    const renderer = new PerformanceRenderer(
      three,
      instanceId,
      (iid) => webRuntime.getInstance(iid) ?? null,
      webRuntime.graphIndex,
      spatialStore,
    );
    registerVideoRenderer(instanceId, this.__PERF3D_KEY, renderer);
    renderer.setCanvas(canvas);

    // Restore saved camera state
    const defId = runtimeStore.getInstanceDefinitionId(instanceId) ?? instanceId;
    const saved = runtimeStore.getPerformanceView(defId);
    if (saved) {
      const orbit = saved.cameras[saved.activeCamera];
      if (orbit) renderer.setOrbit(orbit);
      if (saved.activeCamera !== 'default') {
        renderer.setActiveCamera(saved.activeCamera);
      }
    }
  },

  deactivatePerformanceView(instanceId: string): void {
    const renderer = getVideoRenderer(instanceId, this.__PERF3D_KEY) as PerformanceRenderer | undefined;
    if (renderer) {
      // Save camera state before disposing
      const defId = runtimeStore.getInstanceDefinitionId(instanceId) ?? instanceId;
      const orbit = renderer.getOrbit();
      const existing = runtimeStore.getPerformanceView(defId);
      const activeCamera = renderer.getActiveCamera();
      runtimeStore.setPerformanceView(defId, {
        cameras: { ...existing?.cameras, [activeCamera]: orbit },
        activeCamera,
      });

      renderer.dispose();
      unregisterVideoEntry(instanceId, this.__PERF3D_KEY);
    }
  },

  getPerformanceRenderer(instanceId: string): PerformanceRenderer | null {
    return (getVideoRenderer(instanceId, this.__PERF3D_KEY) as PerformanceRenderer) ?? null;
  },

  setActivePerformanceCamera(instanceId: string, name: string): void {
    const renderer = getVideoRenderer(instanceId, this.__PERF3D_KEY) as PerformanceRenderer | undefined;
    if (!renderer) return;

    const defId = runtimeStore.getInstanceDefinitionId(instanceId) ?? instanceId;
    const existing = runtimeStore.getPerformanceView(defId) ?? { cameras: {}, activeCamera: 'default' };

    // Save current orbit under current camera name
    const currentName = renderer.getActiveCamera();
    const currentOrbit = renderer.getOrbit();
    const cameras = { ...existing.cameras, [currentName]: currentOrbit };

    // Load orbit for new camera (or default from current pan)
    let newOrbit = cameras[name];
    if (!newOrbit) {
      newOrbit = { theta: 0, phi: 0, radius: 80, panX: currentOrbit.panX, panY: 0, panZ: currentOrbit.panZ, roll: 0 };
      cameras[name] = newOrbit;
    }

    renderer.setOrbit(newOrbit);
    renderer.setActiveCamera(name);
    runtimeStore.setPerformanceView(defId, { cameras, activeCamera: name });
  },

  // ─── Performance 2D view ───

  __PERF2D_KEY: '__perf2d',

  activatePerformanceView2D(instanceId: string, canvas: HTMLCanvasElement): void {
    const renderer = new Polyline2DRenderer(
      instanceId,
      (iid) => webRuntime.getInstance(iid) ?? null,
      webRuntime.graphIndex,
    );
    registerVideoRenderer(instanceId, this.__PERF2D_KEY, renderer);
    renderer.setCanvas(canvas);
  },

  deactivatePerformanceView2D(instanceId: string): void {
    const renderer = getVideoRenderer(instanceId, this.__PERF2D_KEY) as Polyline2DRenderer | undefined;
    if (renderer) {
      renderer.dispose();
      unregisterVideoEntry(instanceId, this.__PERF2D_KEY);
    }
  },

  getPerformance2DRenderer(instanceId: string): Polyline2DRenderer | null {
    return (getVideoRenderer(instanceId, this.__PERF2D_KEY) as Polyline2DRenderer) ?? null;
  },

  // ─── Spatial store ───

  getSpatialPosition(instanceId: string, nodeId: string) {
    return spatialStore.get(instanceId, nodeId) ?? null;
  },

  setSpatialPosition(instanceId: string, nodeId: string, x: number, y: number, z: number): void {
    spatialStore.set(instanceId, nodeId, x, y, z);
  },

  // ─── Snapshot / Jukebox ───

  captureSnapshot(name: string): void {
    const definitions = webRuntime.getAllGraphDefinitions();
    const snapshot = captureProjectSnapshot(definitions, name);
    jukeboxController.addSnapshot(snapshot);
  },

  recallSnapshot(name: string): void {
    jukeboxController.recallByName(name);
  },

  removeSnapshot(name: string): void {
    jukeboxController.removeSnapshot(name);
  },

  renameSnapshot(oldName: string, newName: string): void {
    jukeboxController.renameSnapshot(oldName, newName);
  },

  getTweaksFile(): TweaksFile {
    return jukeboxController.getTweaksFile();
  },

  loadTweaksFile(tweaks: TweaksFile): void {
    jukeboxController.loadTweaksFile(tweaks);
  },

  clearTweaks(): void {
    jukeboxController.clear();
  },
};


// ─── Central animation loop ───
// Single RAF loop that ticks all instance expressions per frame.
// Only runs while transport is playing — no needless computation when stopped.
let animFrameId: number | null = null;

function animationTick(): void {
  try {
    webRuntime.getInstance('root')?.animate();
  } catch (e) {
    logger.error('animationTick', `${e}`);
  }
  animFrameId = requestAnimationFrame(animationTick);
}

function startAnimationLoop(): void {
  if (animFrameId !== null) return;
  animFrameId = requestAnimationFrame(animationTick);
}

function stopAnimationLoop(): void {
  if (animFrameId !== null) {
    cancelAnimationFrame(animFrameId);
    animFrameId = null;
  }
}

// Start/stop animation loop with transport
const webTransport = getWebTransport();
webTransport.on('start', () => startAnimationLoop());
webTransport.on('stop', () => stopAnimationLoop());
webTransport.on('pause', () => stopAnimationLoop());

// Clear local history when external state arrives (other user changed something)
runtimeStore.onExternalUpdate = () => historyManager.clear();

// Vite HMR: dispose audio when module reloads
// @ts-ignore - Vite injects import.meta.hot at runtime
if (import.meta.hot) {
  // @ts-ignore
  import.meta.hot.accept();
  // @ts-ignore
  import.meta.hot.dispose(() => {
    logger.info('HMR', 'Disposing audio runtime...');
    stopAnimationLoop();
    // Notify UI to stop RAF loops before disposing nodes
    runtimeStore.emitBeforeDispose();
    webRuntime.dispose();
    serverSync.dispose();
  });
}
